/**
 * HeartVisualEngine — Heart Flow healing visual
 *
 * A warm sun opening in the chest… gently filling everything
 *
 * NO silhouette. NO body. Light IS the experience.
 * "The body is never shown — but always felt"
 *
 * Position: center of chest / sternum — upper-center of screen
 * Scale: EXPANSIVE — fills 30-50%+ of screen, not a point
 * Feel: a field, not a dot. The heart influencing the entire space.
 *
 * Layers (inside out):
 *   1. Core — soft white-gold center
 *   2. Inner glow — warm bright gold
 *   3. Mid field — golden radiance
 *   4. Outer field — very diffused warmth reaching far into background
 *   5. Atmosphere — the whole screen breathes with warmth
 *
 * The glow extends beyond where a body would be.
 * "The body is inside the heart" — not the other way around.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';

const HeartVisualEngine = ({ elapsed = 0, totalDuration = 420, isActive = false }) => {
  const [breath, setBreath] = useState(0.5);
  const [pulse, setPulse] = useState(0.5);
  const [mistAngle, setMistAngle] = useState(0);
  const [depthDrift, setDepthDrift] = useState(1);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;
    startRef.current = Date.now();
    const tick = () => {
      const now = Date.now() - startRef.current;
      const bt = (now % 14000) / 14000;
      setBreath(0.5 + 0.5 * Math.sin(bt * Math.PI * 2 - Math.PI / 2));
      const pt = (now % 10000) / 10000;
      setPulse(0.5 + 0.5 * Math.sin(pt * Math.PI * 2 - Math.PI / 2));
      setMistAngle((now % 35000) / 35000 * Math.PI * 2);
      const dt = (now % 22000) / 22000;
      setDepthDrift(1 + 0.018 * Math.sin(dt * Math.PI * 2));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive]);

  // 4-phase emotional arc
  const phase = useMemo(() => {
    if (elapsed < 60) return 'arrival';
    if (elapsed < 180) return 'expansion';
    if (elapsed < 300) return 'warmth';
    return 'integration';
  }, [elapsed]);

  // Intensity — how bright/present the heart is
  const intensity = useMemo(() => {
    if (phase === 'arrival') {
      const t = Math.min(1, elapsed / 60);
      return (0.35 + t * 0.3) + breath * 0.08;
    }
    if (phase === 'expansion') {
      const t = (elapsed - 60) / 120;
      return (0.65 + t * 0.25) + breath * 0.1;
    }
    if (phase === 'warmth') return 0.92 + breath * 0.06;
    return 0.94 + breath * 0.04;
  }, [phase, elapsed, breath]);

  // Expansion — how large the heart field grows (0 to 1)
  const expansion = useMemo(() => {
    if (phase === 'arrival') {
      const t = Math.min(1, elapsed / 60);
      return 0.35 + t * 0.2;
    }
    if (phase === 'expansion') {
      const t = (elapsed - 60) / 120;
      return 0.55 + t * 0.3;
    }
    if (phase === 'warmth') {
      const t = (elapsed - 180) / 120;
      return 0.85 + t * 0.1;
    }
    return 0.95;
  }, [phase, elapsed]);

  // Density dissolve (emotional blockage clearing)
  const densityDissolve = useMemo(() => {
    if (elapsed < 60) return elapsed / 60 * 0.3;
    if (elapsed < 180) return 0.3 + ((elapsed - 60) / 120) * 0.4;
    return Math.min(1, 0.7 + ((elapsed - 180) / 120) * 0.3);
  }, [elapsed]);

  // Mist positions — golden clouds swirling within the heart field
  const mistClouds = useMemo(() => {
    return [0, 1, 2, 3, 4, 5].map(i => {
      const angle = mistAngle + i * (Math.PI * 2 / 6);
      const radius = 60 + expansion * 80;
      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius * 0.6,
      };
    });
  }, [mistAngle, expansion]);

  // Heart center position — chest/sternum, upper portion of screen
  const heartTop = '35%';
  const heartLeft = '50%';

  return (
    <motion.div
      className="absolute inset-0 overflow-hidden"
      data-testid="heart-visual-engine"
      style={{ transform: `scale(${depthDrift})`, transformOrigin: `50% 35%` }}
    >
      {/* === DEEP WARM DARKNESS — the space itself === */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse 100% 90% at 50% 35%, rgba(35,24,14,${0.5 + intensity * 0.3}) 0%, rgba(10,7,4,1) 60%)`,
      }} />

      {/* === LAYER 5: ATMOSPHERE — entire screen breathes with warmth === */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          opacity: 0.04 + breath * 0.06 + intensity * 0.04,
        }}
        transition={{ duration: 3, ease: 'easeInOut' }}
        style={{
          background: `radial-gradient(ellipse 90% 75% at 50% 35%, rgba(220,185,140,0.18) 0%, rgba(200,170,125,0.04) 45%, transparent 65%)`,
        }}
      />

      {/* === EMOTIONAL DENSITY — dark patches that dissolve as heart opens === */}
      {densityDissolve < 0.85 && (
        <>
          {[
            { x: -60, y: -30, size: 90, opacity: 0.15 },
            { x: 50, y: 20, size: 70, opacity: 0.12 },
            { x: -30, y: 50, size: 60, opacity: 0.1 },
            { x: 25, y: -45, size: 55, opacity: 0.1 },
          ].map((d, i) => (
            <motion.div
              key={`density-${i}`}
              className="absolute pointer-events-none rounded-full"
              style={{
                left: `calc(${heartLeft} + ${d.x}px)`,
                top: `calc(${heartTop} + ${d.y}px)`,
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                width: d.size * (1 - densityDissolve * 0.7),
                height: d.size * (1 - densityDissolve * 0.6),
                opacity: d.opacity * (1 - densityDissolve) * intensity,
              }}
              transition={{ duration: 5, ease: 'easeOut' }}
            >
              <div className="w-full h-full rounded-full" style={{
                background: 'radial-gradient(circle, rgba(15,10,5,0.7) 0%, rgba(15,10,5,0.2) 50%, transparent 70%)',
                filter: 'blur(10px)',
              }} />
            </motion.div>
          ))}
        </>
      )}

      {/* === LAYER 4: OUTER FIELD — very diffused warmth, reaches FAR into background === */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: heartLeft, top: heartTop,
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          width: 400 + expansion * 500 + pulse * 60 + breath * 40,
          height: 350 + expansion * 420 + pulse * 50 + breath * 35,
          opacity: 0.12 + intensity * 0.25 + pulse * 0.06,
        }}
        transition={{ duration: 2.5, ease: 'easeInOut' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(ellipse, rgba(215,180,130,0.2) 0%, rgba(205,170,120,0.06) 35%, rgba(195,160,110,0.015) 55%, transparent 70%)',
          filter: 'blur(40px)',
        }} />
      </motion.div>

      {/* === LAYER 3: MID FIELD — golden radiance, warm presence === */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: heartLeft, top: heartTop,
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          width: 220 + expansion * 280 + pulse * 40 + breath * 25,
          height: 190 + expansion * 230 + pulse * 35 + breath * 20,
          opacity: 0.2 + intensity * 0.45 + pulse * 0.08,
        }}
        transition={{ duration: 2, ease: 'easeInOut' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: `radial-gradient(ellipse 90% 85% at 50% 48%, 
            rgba(245,215,155,${0.45 + pulse * 0.1}) 0%, 
            rgba(235,200,140,${0.2 + pulse * 0.05}) 30%,
            rgba(220,185,125,${0.06}) 55%,
            transparent 72%)`,
          filter: 'blur(8px)',
        }} />
      </motion.div>

      {/* === LAYER 2: INNER GLOW — warm bright gold === */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: heartLeft, top: heartTop,
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          width: 100 + expansion * 140 + pulse * 25 + breath * 15,
          height: 90 + expansion * 120 + pulse * 20 + breath * 12,
          opacity: 0.35 + intensity * 0.5 + pulse * 0.1,
        }}
        transition={{ duration: 1.8, ease: 'easeInOut' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: `radial-gradient(ellipse 80% 75% at 50% 48%, 
            rgba(255,232,175,${0.85 + pulse * 0.1}) 0%, 
            rgba(250,220,160,${0.4 + pulse * 0.08}) 30%,
            rgba(240,205,145,${0.12}) 55%,
            transparent 70%)`,
          filter: 'blur(2px)',
        }} />
      </motion.div>

      {/* === LAYER 1: CORE — soft white-gold center === */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: heartLeft, top: heartTop,
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          width: 35 + expansion * 50 + pulse * 12,
          height: 30 + expansion * 42 + pulse * 10,
          opacity: 0.5 + intensity * 0.42 + pulse * 0.1,
        }}
        transition={{ duration: 1.4, ease: 'easeInOut' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(255,248,225,0.95) 0%, rgba(255,240,200,0.55) 35%, rgba(250,228,180,0.15) 60%, transparent 75%)',
        }} />
      </motion.div>

      {/* === SOFT ROSE UNDERTONE — warmth beneath gold === */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: heartLeft, top: heartTop,
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          width: 180 + expansion * 160 + breath * 20,
          height: 160 + expansion * 130 + breath * 16,
          opacity: 0.05 + intensity * 0.1 + pulse * 0.03,
        }}
        transition={{ duration: 3, ease: 'easeInOut' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(215,160,145,0.3) 0%, rgba(205,150,135,0.08) 40%, transparent 60%)',
          filter: 'blur(18px)',
        }} />
      </motion.div>

      {/* === INNER MIST — golden clouds swirling within heart field === */}
      {expansion > 0.4 && mistClouds.map((pos, i) => (
        <motion.div
          key={`mist-${i}`}
          className="absolute pointer-events-none rounded-full"
          style={{
            left: `calc(${heartLeft} + ${pos.x}px)`,
            top: `calc(${heartTop} + ${pos.y}px)`,
            transform: 'translate(-50%, -50%)',
          }}
          animate={{
            width: 30 + expansion * 45 + pulse * 12,
            height: 25 + expansion * 35 + pulse * 10,
            opacity: (expansion - 0.3) * 0.35 * intensity,
          }}
          transition={{ duration: 3.5, ease: 'easeInOut' }}
        >
          <div className="w-full h-full rounded-full" style={{
            background: `radial-gradient(ellipse, rgba(${228 + i * 5},${198 + i * 3},${150 - i * 6},0.4) 0%, transparent 55%)`,
            filter: 'blur(8px)',
          }} />
        </motion.div>
      ))}

      {/* === CONNECTION LAYER — glow extends FAR beyond body, filling the entire space === */}
      {/* "The body is inside the heart, not the heart inside the body" */}
      {(phase === 'warmth' || phase === 'integration') && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{
            opacity: phase === 'warmth'
              ? Math.min(0.35, ((elapsed - 180) / 120) * 0.35) * (0.85 + breath * 0.15)
              : 0.35 + Math.min(0.25, ((elapsed - 300) / 120) * 0.25) * (0.85 + breath * 0.15),
          }}
          transition={{ duration: 6, ease: 'easeInOut' }}
        >
          <div className="absolute inset-0" style={{
            background: `radial-gradient(ellipse 95% 90% at 50% 35%, rgba(240,218,165,0.2) 0%, rgba(230,205,150,0.08) 25%, rgba(220,195,140,0.03) 45%, transparent 65%)`,
          }} />
        </motion.div>
      )}

      {/* === INTEGRATION — warmth filling the entire space === */}
      {phase === 'integration' && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 65% 55% at 50% 35%, rgba(240,218,160,0.25) 0%, rgba(225,200,140,0.06) 40%, transparent 55%)`,
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: Math.min(0.6, ((elapsed - 300) / 120) * 0.6) * (0.8 + breath * 0.2),
          }}
          transition={{ duration: 5 }}
        />
      )}

      {/* === AMBIENT PARTICLES — warm golden dust drifting gently === */}
      {[...Array(10)].map((_, i) => (
        <motion.div
          key={`p-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 2 + (i % 3) * 0.8,
            height: 2 + (i % 3) * 0.8,
            background: `rgba(230,200,150,${0.08 + (i % 3) * 0.04})`,
            left: `${20 + (i * 7) % 60}%`,
            top: `${15 + (i * 6) % 45}%`,
          }}
          animate={{
            y: [0, -12 - (i % 4) * 4, 0],
            x: [0, -4 + (i % 3) * 4, 0],
            opacity: [0, 0.1 + intensity * 0.08, 0],
          }}
          transition={{
            duration: 14 + (i % 4) * 3,
            repeat: Infinity,
            delay: i * 1.8,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* === MICRO PARTICLES — almost imperceptible, alive === */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={`micro-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 1.5,
            height: 1.5,
            background: `rgba(240,218,170,${0.05 + (i % 2) * 0.03})`,
            left: `${30 + (i * 6) % 40}%`,
            top: `${25 + (i * 5) % 30}%`,
          }}
          animate={{
            y: [0, -5 - (i % 2) * 3, 0],
            x: [0, 2 - (i % 3), 0],
            opacity: [0, 0.06 + intensity * 0.05, 0],
          }}
          transition={{
            duration: 20 + (i % 3) * 5,
            repeat: Infinity,
            delay: i * 3,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* === UPWARD RADIANCE — very subtle upward drift of light === */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: heartLeft, top: heartTop,
          transform: 'translate(-50%, -50%)',
          width: 120 + expansion * 80,
          height: 200 + expansion * 150,
        }}
        animate={{
          opacity: [0, 0.04 + intensity * 0.04, 0],
          y: [0, -20, -40],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeOut',
        }}
      >
        <div className="w-full h-full" style={{
          background: 'linear-gradient(to top, rgba(225,195,145,0.12) 0%, rgba(225,195,145,0.03) 40%, transparent 70%)',
          filter: 'blur(20px)',
          borderRadius: '50% 50% 50% 50% / 30% 30% 70% 70%',
        }} />
      </motion.div>

      {/* === RELEASE SPARKS — golden sparks drifting up === */}
      {phase !== 'arrival' && [...Array(4)].map((_, i) => (
        <motion.div
          key={`spark-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 2.5,
            height: 2.5,
            background: `rgba(255,235,180,${0.2 + pulse * 0.1})`,
            left: `calc(${heartLeft} + ${-15 + i * 10}px)`,
            top: heartTop,
            filter: 'blur(0.5px)',
          }}
          animate={{
            y: [0, -30 - i * 12, -55 - i * 18],
            x: [0, (-6 + i * 4), (-4 + i * 5)],
            opacity: [0.2 * intensity, 0.1 * intensity, 0],
            scale: [1, 0.6, 0.2],
          }}
          transition={{
            duration: 7 + i * 2,
            repeat: Infinity,
            delay: i * 2.5,
            ease: 'easeOut',
          }}
        />
      ))}
    </motion.div>
  );
};

export default HeartVisualEngine;
